import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { connectorsApi } from '@/api/connectors'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { invalidateCopilotAuthAvailability } from './useProjectSettings'
import { useTenantKey } from './useTenantKey'

function describeError(error: unknown, fallback: string): string {
  return extractValidationMessage(error) ?? getErrorMessage(error) ?? fallback
}

export type DeviceFlowState =
  | { phase: 'idle' }
  | { phase: 'initiating' }
  | { phase: 'awaiting_user'; flowId: string; userCode: string; verificationUri: string; expiresIn: number }
  | { phase: 'complete' }
  | { phase: 'expired' }
  | { phase: 'access_denied' }
  | { phase: 'error'; message: string }

export function useGitHubDeviceFlow(projectId: string, onComplete?: () => void) {
  const [state, setState] = useState<DeviceFlowState>({ phase: 'idle' })
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef(false)
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  const stopPolling = useCallback(() => {
    abortRef.current = true
    if (pollingRef.current) {
      clearTimeout(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  const pollLoop = useCallback(async (flowId: string, interval: number) => {
    if (abortRef.current) return

    try {
      const result = await connectorsApi.pollCopilotDeviceFlow(projectId, flowId)

      if (abortRef.current) return

      switch (result.status) {
        case 'Complete': {
          stopPolling()
          setState({ phase: 'complete' })
          queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'connectors') })
          queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-trigger-schema') })
          invalidateCopilotAuthAvailability(queryClient)
          onComplete?.()
          break
        }
        case 'Pending':
        case 'SlowDown': {
          const nextInterval = result.nextPollIntervalSeconds ?? interval
          pollingRef.current = setTimeout(() => pollLoop(flowId, nextInterval), nextInterval * 1000)
          break
        }
        case 'Expired':
          stopPolling()
          setState({ phase: 'expired' })
          break
        case 'AccessDenied':
          stopPolling()
          setState({ phase: 'access_denied' })
          break
        default:
          stopPolling()
          setState({ phase: 'error', message: result.error ?? 'Unknown error' })
      }
    } catch (err) {
      if (!abortRef.current) {
        stopPolling()
        setState({ phase: 'error', message: describeError(err, 'Could not check the GitHub sign-in. Try again.') })
      }
    }
  }, [stopPolling, queryClient, tenantKey, projectId, onComplete])

  const initiate = useCallback(async () => {
    stopPolling()
    abortRef.current = false
    setState({ phase: 'initiating' })

    try {
      const data = await connectorsApi.initiateCopilotDeviceFlow(projectId)

      if (abortRef.current) return

      setState({
        phase: 'awaiting_user',
        flowId: data.flowId,
        userCode: data.userCode,
        verificationUri: data.verificationUri,
        expiresIn: data.expiresIn,
      })
      pollingRef.current = setTimeout(() => pollLoop(data.flowId, data.interval), data.interval * 1000)
    } catch (err) {
      if (!abortRef.current) {
        setState({ phase: 'error', message: describeError(err, 'Could not start the GitHub sign-in. Try again.') })
      }
    }
  }, [stopPolling, pollLoop, projectId])

  const reset = useCallback(() => {
    stopPolling()
    setState({ phase: 'idle' })
  }, [stopPolling])

  return { state, initiate, reset, cancel: stopPolling }
}
